// src/components/LoginForm.tsx
//
// Pantalla de acceso — lenguaje visual del sistema operativo «Orion».
//
// Diseño partido: panel de marca (índigo profundo + símbolo Orion) y formulario
// limpio. En móvil el panel de marca se compacta arriba.
//
// 🔒 SOLO PRESENTACIÓN. La lógica de acceso es la misma de siempre: mismo
// endpoint (login/registerStart/registerVerifyEmail/forgotPassword de lib/api),
// mismo guardado de token (`auth_token`, `org_id`, `user_roles`,
// `orion_username`), mismo evento `auth:token-ready` y mismo `onSuccess`.
//
// El login es COMPARTIDO por todas las orgs → por defecto pinta la marca Orion.
// Se pinta la marca de la org (nombre, logo, avatar, paleta) cuando:
//  - el dominio es de una org (dashboard.onepercent.mx → onepercent vía el
//    resolver host→org de Django, OrgDomain); los dominios de la plataforma
//    (archetype «platform», p.ej. dashboard.brainstream.pro) siguen con Orion;
//  - o es una celda dedicada / marca blanca (/org/info sin org_id resuelve la
//    org por defecto de esa caja y trae agent_photo/slogan/white_label).
// Con white_label se oculta el «powered by».

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  Lock,
  Mail,
  MessageCircle,
  Moon,
  Phone,
  Sun,
  User,
  X,
} from 'lucide-react';

import { login, registerStart, registerVerifyEmail, forgotPassword } from '../lib/api';
import { useBranding } from '@/context/BrandingContext';
import { apiUrl } from '@/config';
import { ORION_BRAND } from '@/modules/orion-shell/brand';
import { aplicarMarcaPwa } from '@/lib/pwa';

interface LoginFormProps {
  onSuccess: () => void;
}

// ── Marca del host (opcional) ─────────────────────────────────────────────

interface HostBrand {
  name: string;
  logo: string | null;
  agentPhoto: string | null;
  slogan: string;
  whiteLabel: boolean;
  /** Fondo del panel de marca y extremos del degradado del botón (ya validados AA). */
  panelBg: string | null;
  gradient: [string, string] | null;
  /** Halos del panel con los acentos de la org (decorativos). */
  glow: [string, string] | null;
  /** Acento de la org legible sobre blanco (enlaces y foco, tema claro). */
  link: string | null;
}

type RGB = [number, number, number];

function parseHex(v: unknown): RGB | null {
  if (typeof v !== 'string') return null;
  const m = v.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luminance([r, g, b]: RGB): number {
  const f = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

function contrastWhite(c: RGB): number {
  return 1.05 / (luminance(c) + 0.05);
}

/** Oscurece el color (mismo tono) hasta que el texto blanco encima dé AA. */
function darkenForWhite(c: RGB): RGB {
  let out = c;
  for (let i = 0; i < 24 && contrastWhite(out) < 4.5; i++) {
    out = out.map((x) => Math.round(x * 0.93)) as RGB;
  }
  return out;
}

/** /org/info → marca del host, o null si es la plataforma compartida (sin marca propia).
 *  `porDominio`: la org salió del dominio → siempre lleva su marca. */
function hostBrandFrom(raw: unknown, porDominio = false): HostBrand | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const hasOwn = porDominio || !!(d.white_label || d.agent_photo || (typeof d.slogan === 'string' && d.slogan.trim()));
  if (!hasOwn) return null;
  const p = d.palette || {};
  const bg = parseHex(p.bg_dark);
  const a1 = parseHex(p.acc1);
  const a2 = parseHex(p.acc2);
  const a3 = parseHex(p.acc3);
  // Texto blanco del panel: el fondo debe dar AA; si no, se queda el índigo de Orion.
  const panelBg = bg && contrastWhite(bg) >= 4.5 ? (p.bg_dark as string) : null;
  // Botón: degradado con los acentos de la org, oscurecidos lo justo para que
  // el texto blanco dé AA (el naranja de OnePercent queda naranja, no morado).
  const accents = [a1, a2, a3].filter((c): c is RGB => !!c);
  const from = accents[0] ? darkenForWhite(accents[0]) : null;
  const to = accents.length > 1 ? darkenForWhite(accents[accents.length - 1]) : from;
  const hex = (c: RGB) => `#${c.map((x) => x.toString(16).padStart(2, '0')).join('')}`;
  return {
    name: typeof d.name === 'string' ? d.name : '',
    logo: typeof d.logo === 'string' && /^https:\/\//.test(d.logo) ? d.logo : null,
    agentPhoto: typeof d.agent_photo === 'string' && d.agent_photo ? d.agent_photo : null,
    slogan: typeof d.slogan === 'string' ? d.slogan.trim() : '',
    whiteLabel: !!d.white_label,
    panelBg,
    gradient: from && to ? [hex(from), hex(to)] : null,
    link: from ? hex(from) : null,
    glow: a1 && a2 ? [`rgba(${a1.join(', ')}, .45)`, `rgba(${a2.join(', ')}, .55)`] : null,
  };
}

/** Org dueña del dominio (OrgDomain en Django). `dashboard.onepercent.mx` se
 *  busca tal cual y, si no está registrado, como `onepercent.mx`. Los dominios
 *  de la plataforma (archetype «platform») no cuentan: ahí se queda Orion. */
async function orgDelDominio(
  hostname: string,
  json: (url: string) => Promise<any>, // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<string | null> {
  const host = hostname.toLowerCase();
  const apex = host.replace(/^(staging-)?dashboard\./, '');
  for (const h of apex !== host ? [host, apex] : [host]) {
    const r = await json(`/django-api/api/v1/site-config/resolve?host=${encodeURIComponent(h)}`).catch(() => null);
    if (r && typeof r.org_id === 'string' && r.org_id) {
      return r.archetype === 'platform' ? null : r.org_id;
    }
  }
  return null;
}

// ── Mensajes claros ───────────────────────────────────────────────────────

/** Traduce el error técnico a algo accionable (solo texto; la lógica no cambia). */
function friendlyError(raw: string, flow: 'login' | 'register'): string {
  const m = (raw || '').trim();
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(m)) {
    return 'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.';
  }
  if (/429|too many/i.test(m)) {
    return 'Demasiados intentos seguidos. Espera un momento y vuelve a intentarlo.';
  }
  if (flow === 'login' && /sesi[oó]n expirada|incorrect|invalid|401|credencial/i.test(m)) {
    return 'Usuario o contraseña incorrectos. Revísalos e inténtalo de nuevo.';
  }
  if (/request failed with status 5\d\d|internal server/i.test(m)) {
    return 'El servidor no respondió como esperábamos. Inténtalo de nuevo en unos minutos.';
  }
  return m || 'No pudimos completar la operación. Inténtalo de nuevo.';
}

// ── Tema (misma preferencia que <Layout> y el shell: `orion_theme`) ───────

const THEME_KEY = 'orion_theme';

function useThemeToggle(): ['light' | 'dark', () => void] {
  const read = () =>
    (document.documentElement.getAttribute('data-theme') as 'light' | 'dark' | null) ||
    ((localStorage.getItem(THEME_KEY) as 'light' | 'dark' | null) ?? 'light');
  const [mode, setMode] = useState<'light' | 'dark'>(() => (read() === 'dark' ? 'dark' : 'light'));
  const toggle = useCallback(() => {
    setMode((m) => {
      const next = m === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        /* modo privado */
      }
      return next;
    });
  }, []);
  return [mode, toggle];
}

// ── Piezas de UI ──────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg className="h-4 w-4 motion-safe:animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity=".3" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function Field({
  id,
  label,
  icon,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  icon: React.ReactNode;
  hint?: string;
  error?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-[color:var(--ol-fg)]">
        {label}
      </label>
      <div className={`ol-input-wrap ${error ? 'ol-input-wrap--error' : ''}`}>
        <span aria-hidden className="ol-input-icon">
          {icon}
        </span>
        {children}
      </div>
      {hint && (
        <p id={`${id}-hint`} className="mt-1.5 text-xs text-[color:var(--ol-muted)]">
          {hint}
        </p>
      )}
    </div>
  );
}

function ErrorNote({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <div id={id} role="alert" className="ol-alert ol-alert--error">
      <AlertCircle size={18} aria-hidden className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

// ── Pantalla ──────────────────────────────────────────────────────────────

export function LoginForm({ onSuccess }: LoginFormProps) {
  const { orgName } = useBranding();
  const [host, setHost] = useState<HostBrand | null>(null);

  useEffect(() => {
    let alive = true;
    const json = (url: string) =>
      fetch(url, { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null));
    (async () => {
      const org = await orgDelDominio(window.location.hostname, json);
      const info = await json(org ? `${apiUrl('/org/info')}?org_id=${encodeURIComponent(org)}` : apiUrl('/org/info'));
      const brand = hostBrandFrom(info, !!org);
      if (!alive) return;
      setHost(brand);
      if (brand) {
        aplicarMarcaPwa({
          nombre: brand.name || 'Orion',
          logo: brand.logo,
          fondo: '#FFFFFF',
          tema: brand.panelBg || '#1E1B4B',
        });
      }
    })().catch(() => {
      /* pre-auth / red: marca Orion */
    });
    return () => {
      alive = false;
    };
  }, []);

  // Nombre con el que se habla al usuario (recuperar acceso, etc.).
  const brandName = (host?.name || orgName || 'Orion').trim();

  const [isRegister, setIsRegister] = useState<boolean>(false);
  // Registration Flow: 'form' -> 'verify' -> 'pending'
  const [regStep, setRegStep] = useState<'form' | 'verify' | 'pending'>('form');
  const [regId, setRegId] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [regMessage, setRegMessage] = useState<string>('');

  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [telefono, setTelefono] = useState<string>('');
  const [forgotTelefono, setForgotTelefono] = useState<string>('');
  const [remember, setRemember] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [forgotStatus, setForgotStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [forgotMessage, setForgotMessage] = useState<string>('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [mode, toggleMode] = useThemeToggle();

  const ids = {
    user: useId(),
    pass: useId(),
    email: useId(),
    tel: useId(),
    code: useId(),
    remember: useId(),
    error: useId(),
    title: useId(),
  };
  const errorRef = useRef<HTMLDivElement | null>(null);

  // Auto-login si hay token guardado
  useEffect(() => {
    const savedUser = localStorage.getItem('orion_username');
    // ahora guardamos el token bajo 'auth_token' para ser consistente con api.ts
    const savedToken = localStorage.getItem('auth_token');
    if (savedUser) setUsername(savedUser);
    if (savedToken) {
      setStatus('success');
      setTimeout(onSuccess, 600); // saltar login directo
    }
  }, [onSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setStatus('loading');
    try {
      // NEW: Registration Flow
      if (isRegister) {
        if (regStep === 'form') {
          // Paso 1: Iniciar registro (envía código)
          const res = await registerStart(username, email, telefono, password);
          if (res.success && res.registration_id) {
            setRegId(res.registration_id);
            setRegMessage(res.message);
            setRegStep('verify');
            setStatus('idle'); // Stop loading to show verify form
            return;
          } else if (res.registration_id && res.message.includes('pendiente')) {
            // Caso especial: Ya estaba pendiente
            setRegId(res.registration_id);
            setRegMessage(res.message);
            setRegStep('pending');
            setStatus('success');
            return;
          } else {
            throw new Error(res.message || 'Error al iniciar registro');
          }
        } else if (regStep === 'verify') {
          // Paso 2: Verificar código
          const res = await registerVerifyEmail(regId, verificationCode);
          if (res.success) {
            setRegMessage(res.message);
            setRegStep('pending');
            setStatus('success');
            return;
          } else {
            throw new Error(res.message || 'Error al verificar código');
          }
        }
        return;
      }

      // LOGIN FLOW
      const result = await login(username, password);

      // Extraer token y org_id de la respuesta
      const accessToken = result?.access_token || result?.token;
      const orgIdFromResponse = result?.org_id;

      if (!accessToken) {
        throw new Error('No se recibió token de autenticación del servidor');
      }

      // Guardar token y datos de sesión
      localStorage.setItem('auth_token', accessToken);
      // Notificar a los componentes que esperan auth (ej. TemplatePicker race condition fix)
      window.dispatchEvent(new CustomEvent('auth:token-ready'));
      if (orgIdFromResponse) {
        localStorage.setItem('org_id', orgIdFromResponse);
      }
      // Guardar roles para control de módulos
      if (result?.roles) {
        localStorage.setItem('user_roles', JSON.stringify(result.roles));
      }
      if (remember && !isRegister) {
        localStorage.setItem('orion_username', username);
      }

      // Verificar que se guardó correctamente
      const savedToken = localStorage.getItem('auth_token');
      if (!savedToken || savedToken !== accessToken) {
        throw new Error('Error al guardar la sesión. Intenta de nuevo.');
      }

      setStatus('success');
      setTimeout(onSuccess, 200);
    } catch (err) {
      console.error('❌ Error en login/registro:', err);
      const message =
        err instanceof Error ? err.message :
        typeof err === 'string' ? err :
        'Error de autenticación. Por favor verifica tus credenciales.';
      setError(friendlyError(message, isRegister ? 'register' : 'login'));
      setStatus('error');
    }
  };

  // El error se anuncia (role=alert) y el foco vuelve al primer campo.
  useEffect(() => {
    if (status === 'error' && error) {
      const first = document.getElementById(isRegister && regStep === 'verify' ? ids.code : ids.user) as HTMLInputElement | null;
      first?.focus();
    }
  }, [status, error, isRegister, regStep, ids.code, ids.user]);

  const loading = status === 'loading';
  const hasError = status === 'error' && !!error;

  const brandStyle = useMemo<React.CSSProperties>(() => {
    const s: Record<string, string> = {};
    if (host?.panelBg) s['--ol-panel'] = host.panelBg;
    if (host?.gradient) s['--ol-brand-gradient'] = `linear-gradient(135deg, ${host.gradient[0]} 0%, ${host.gradient[1]} 100%)`;
    if (host?.link) {
      s['--ol-host-link'] = host.link;
      s['--ol-host-ring'] = `${host.link}38`;
      s['--ol-btn-shadow'] = `${host.link}99`;
    }
    if (host?.glow) {
      s['--ol-glow-a'] = host.glow[0];
      s['--ol-glow-b'] = host.glow[1];
    }
    return s as React.CSSProperties;
  }, [host]);

  const switchMode = () => {
    setIsRegister(!isRegister);
    setRegStep('form');
    setError('');
    setStatus('idle');
  };

  const title = isRegister
    ? regStep === 'verify'
      ? 'Verifica tu correo'
      : regStep === 'pending'
        ? 'Solicitud enviada'
        : 'Solicita acceso'
    : 'Inicia sesión';
  const subtitle = isRegister
    ? regStep === 'form'
      ? `Crea tu solicitud para entrar a ${brandName}. Un administrador la aprueba.`
      : ''
    : host
      ? `Entra a ${brandName} con tu usuario y contraseña.`
      : 'Entra a tu organización con tu usuario y contraseña.';

  return (
    <div className="orion-login" style={brandStyle}>
      {/* ── Panel de marca ── */}
      <aside className="ol-brand" aria-label={host ? brandName : 'Orion'}>
        <div aria-hidden className="ol-brand-glow" />
        {!host && (
          <img
            aria-hidden
            alt=""
            src={ORION_BRAND.symbol}
            className="ol-brand-symbol"
            draggable={false}
          />
        )}

        <div className="ol-brand-inner">
          {host ? (
            <div className="flex items-center gap-3">
              {host.logo ? (
                <span className="ol-host-logo">
                  <img src={host.logo} alt={brandName} className="h-8 w-auto max-w-[160px] object-contain" />
                </span>
              ) : (
                <span className="text-lg font-semibold tracking-tight text-white">{brandName}</span>
              )}
            </div>
          ) : (
            <img
              src={ORION_BRAND.logoDark}
              alt="Orion · Organizational Intelligence"
              width={194}
              height={60}
              className="ol-brand-logo"
              draggable={false}
            />
          )}

          <div className="ol-brand-copy">
            {host?.agentPhoto && (
              <img
                src={host.agentPhoto}
                alt=""
                aria-hidden
                className="ol-host-avatar"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <p className="ol-brand-title">
              {host ? host.slogan || brandName : 'El sistema operativo de inteligencia para tu organización'}
            </p>
            {!host && (
              <p className="ol-brand-sub">
                Tus agentes, conversaciones, clientes y sitio web en un solo lugar.
              </p>
            )}
          </div>

          {!host?.whiteLabel && (
            <p className="ol-brand-foot">
              {host ? 'Con tecnología de Orion' : 'Orion · Organizational Intelligence'}
            </p>
          )}
        </div>
      </aside>

      {/* ── Formulario ── */}
      <main className="ol-main">
        <div className="ol-topbar">
          <button
            type="button"
            onClick={toggleMode}
            className="ol-icon-btn"
            aria-label={mode === 'dark' ? 'Usar tema claro' : 'Usar tema oscuro'}
            title={mode === 'dark' ? 'Tema claro' : 'Tema oscuro'}
          >
            {mode === 'dark' ? <Sun size={18} aria-hidden /> : <Moon size={18} aria-hidden />}
          </button>
        </div>

        <div className="ol-form-wrap">
          <h1 id={ids.title} className="text-[26px] font-semibold leading-tight tracking-tight text-[color:var(--ol-fg)] sm:text-[28px]">
            {title}
          </h1>
          {subtitle && <p className="mt-2 text-sm leading-relaxed text-[color:var(--ol-muted)]">{subtitle}</p>}

          <form
            className="mt-7"
            onSubmit={handleSubmit}
            autoComplete="on"
            aria-labelledby={ids.title}
            aria-busy={loading}
            noValidate={false}
          >
            {isRegister && regStep === 'pending' ? (
              /* VISTA: RESULTADO DE REGISTRO (PENDING) */
              <div className="ol-alert ol-alert--ok flex-col items-start gap-2" role="status">
                <span className="flex items-center gap-2 font-semibold">
                  <Check size={18} aria-hidden /> ¡Listo! Recibimos tu solicitud.
                </span>
                {regMessage && <span>{regMessage}</span>}
                <span className="text-[color:var(--ol-muted)]">
                  Te avisaremos por WhatsApp cuando un administrador la apruebe.
                </span>
                <button type="button" onClick={() => { setIsRegister(false); setRegStep('form'); setStatus('idle'); setError(''); }} className="ol-link mt-1">
                  <ArrowLeft size={15} aria-hidden /> Volver a iniciar sesión
                </button>
              </div>
            ) : isRegister && regStep === 'verify' ? (
              /* VISTA: VERIFICAR CÓDIGO */
              <div className="space-y-5">
                <div className="ol-alert ol-alert--info" role="status">
                  <Mail size={18} aria-hidden className="mt-0.5 shrink-0" />
                  <span>
                    {regMessage || 'Te enviamos un código a tu correo.'} Revisa también la carpeta de spam.
                  </span>
                </div>
                <Field id={ids.code} label="Código de verificación" icon={<Lock size={18} />} hint="6 dígitos" error={hasError}>
                  <input
                    id={ids.code}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    maxLength={6}
                    className="ol-input font-mono tracking-[0.4em]"
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    aria-invalid={hasError}
                    aria-describedby={`${ids.code}-hint${hasError ? ` ${ids.error}` : ''}`}
                  />
                </Field>

                {hasError && <ErrorNote id={ids.error}>{error}</ErrorNote>}

                <div className="flex gap-3">
                  <button type="button" onClick={() => { setRegStep('form'); setError(''); setStatus('idle'); }} className="ol-btn-secondary flex-1">
                    Atrás
                  </button>
                  <button type="submit" disabled={loading} className="ol-btn-brand flex-[2]">
                    {loading ? (<><Spinner /> Verificando…</>) : 'Verificar código'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    setError('');
                    setStatus('loading');
                    try {
                      const res = await registerStart(username, email, telefono, password);
                      setRegMessage(res.message);
                      setStatus('idle');
                    } catch (e) {
                      setError(friendlyError(e instanceof Error ? e.message : 'Error al reenviar código', 'register'));
                      setStatus('error');
                    }
                  }}
                  className="ol-link"
                >
                  ¿No te llegó? Reenviar código
                </button>
              </div>
            ) : (
              /* VISTA: LOGIN O FORMULARIO DE REGISTRO INICIAL */
              <div className="space-y-4">
                <Field id={ids.user} label="Usuario" icon={<User size={18} />} error={hasError}>
                  <input
                    id={ids.user}
                    name="username"
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    required
                    className="ol-input"
                    placeholder="Tu usuario"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    aria-invalid={hasError}
                    aria-describedby={hasError ? ids.error : undefined}
                  />
                </Field>

                <Field id={ids.pass} label="Contraseña" icon={<Lock size={18} />} error={hasError}>
                  <input
                    id={ids.pass}
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={isRegister ? 'new-password' : 'current-password'}
                    required
                    className="ol-input pr-12"
                    placeholder="Tu contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-invalid={hasError}
                    aria-describedby={hasError ? ids.error : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="ol-eye"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    aria-pressed={showPassword}
                    aria-controls={ids.pass}
                  >
                    {showPassword ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
                  </button>
                </Field>

                {isRegister && (
                  <>
                    <Field id={ids.email} label="Correo electrónico" icon={<Mail size={18} />}>
                      <input
                        id={ids.email}
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        className="ol-input"
                        placeholder="nombre@empresa.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </Field>
                    <Field id={ids.tel} label="Teléfono (WhatsApp)" icon={<Phone size={18} />} hint="10 dígitos, sin código de país">
                      <input
                        id={ids.tel}
                        name="telefono"
                        type="tel"
                        autoComplete="tel-national"
                        inputMode="tel"
                        required
                        className="ol-input"
                        placeholder="5512345678"
                        value={telefono}
                        onChange={(e) => setTelefono(e.target.value)}
                        aria-describedby={`${ids.tel}-hint`}
                      />
                    </Field>
                  </>
                )}

                {!isRegister && (
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pt-0.5">
                    <label htmlFor={ids.remember} className="flex cursor-pointer items-center gap-2 text-sm text-[color:var(--ol-fg)]">
                      <input
                        id={ids.remember}
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="ol-check"
                      />
                      Recordar mi usuario
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setForgotStatus('idle');
                        setForgotMessage('');
                        setForgotTelefono('');
                      }}
                      className="ol-link"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                )}

                <div ref={errorRef} aria-live="polite">
                  {hasError && <ErrorNote id={ids.error}>{error}</ErrorNote>}
                  {status === 'success' && !isRegister && (
                    <div className="ol-alert ol-alert--ok" role="status">
                      <Check size={18} aria-hidden className="mt-0.5 shrink-0" />
                      <span>Sesión iniciada. Entrando…</span>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={loading || status === 'success'} className="ol-btn-brand w-full">
                  {loading ? (
                    <>
                      <Spinner /> {isRegister ? 'Enviando…' : 'Entrando…'}
                    </>
                  ) : (
                    <>
                      {isRegister ? 'Continuar' : 'Iniciar sesión'} <ArrowRight size={16} aria-hidden />
                    </>
                  )}
                </button>
              </div>
            )}
          </form>

          {!(isRegister && regStep !== 'form') && (
            <p className="mt-7 border-t border-[color:var(--ol-border)] pt-5 text-center text-sm text-[color:var(--ol-muted)]">
              {isRegister ? '¿Ya tienes cuenta? ' : '¿Aún no tienes cuenta? '}
              <button type="button" onClick={switchMode} className="ol-link inline">
                {isRegister ? 'Inicia sesión' : 'Solicita acceso'}
              </button>
            </p>
          )}

          {!host && (
            <p className="mt-8 text-center text-xs text-[color:var(--ol-muted)]">Powered by Brainstream</p>
          )}
        </div>
      </main>

      {showForgotPassword && (
        <ForgotPasswordDialog
          brandName={brandName}
          telefono={forgotTelefono}
          setTelefono={setForgotTelefono}
          status={forgotStatus}
          message={forgotMessage}
          onClose={() => setShowForgotPassword(false)}
          onSubmit={async () => {
            setForgotStatus('loading');
            setForgotMessage('');
            try {
              const result = await forgotPassword(forgotTelefono);
              if (result.success) {
                setForgotStatus('success');
                setForgotMessage(result.message);
              } else {
                setForgotStatus('error');
                setForgotMessage('Ocurrió un error. Intenta de nuevo.');
              }
            } catch (err) {
              setForgotStatus('error');
              setForgotMessage(friendlyError(err instanceof Error ? err.message : 'Error al procesar la solicitud', 'register'));
            }
          }}
        />
      )}
    </div>
  );
}

// ── Recuperar acceso (diálogo accesible) ─────────────────────────────────

function ForgotPasswordDialog({
  brandName,
  telefono,
  setTelefono,
  status,
  message,
  onClose,
  onSubmit,
}: {
  brandName: string;
  telefono: string;
  setTelefono: (v: string) => void;
  status: 'idle' | 'loading' | 'success' | 'error';
  message: string;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const titleId = useId();
  const inputId = useId();
  const ref = useRef<HTMLDivElement | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    opener.current = document.activeElement as HTMLElement | null;
    (ref.current?.querySelector<HTMLElement>('input') ?? ref.current?.querySelector<HTMLElement>('button'))?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && ref.current) {
        const f = Array.from(ref.current.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled])'));
        if (f.length === 0) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener.current?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="ol-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId} className="ol-dialog">
        <button type="button" onClick={onClose} className="ol-icon-btn absolute right-3 top-3" aria-label="Cerrar">
          <X size={18} aria-hidden />
        </button>
        <span aria-hidden className="ol-dialog-icon">
          <MessageCircle size={22} />
        </span>
        <h2 id={titleId} className="mt-4 text-xl font-semibold tracking-tight text-[color:var(--ol-fg)]">
          Recupera tu acceso
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--ol-muted)]">
          Escribe tu número de WhatsApp y {brandName} te escribirá para ayudarte a recuperar tu acceso.
        </p>

        <form
          className="mt-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <Field id={inputId} label="Tu número de WhatsApp" icon={<Phone size={18} />} hint="10 dígitos, sin código de país" error={status === 'error'}>
            <input
              id={inputId}
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              required
              placeholder="5512345678"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              disabled={status === 'loading' || status === 'success'}
              className="ol-input"
              aria-describedby={`${inputId}-hint`}
              aria-invalid={status === 'error'}
            />
          </Field>

          {message && (
            status === 'success' ? (
              <div className="ol-alert ol-alert--ok" role="status">
                <Check size={18} aria-hidden className="mt-0.5 shrink-0" />
                <span>{message}</span>
              </div>
            ) : (
              <ErrorNote>{message}</ErrorNote>
            )
          )}

          {status !== 'success' ? (
            <button type="submit" disabled={status === 'loading'} className="ol-btn-brand w-full">
              {status === 'loading' ? (<><Spinner /> Enviando…</>) : (<><MessageCircle size={16} aria-hidden /> Pedir ayuda por WhatsApp</>)}
            </button>
          ) : (
            <button type="button" onClick={onClose} className="ol-btn-secondary w-full">
              Entendido, esperaré el mensaje
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
